import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Bot, Cpu, Mic, Volume2, Sliders, Wand2, Keyboard, Waves,
  ShieldAlert, ChevronDown, ChevronRight, Plus, X, Wrench, BookOpen, Brain, MessageSquare, Radio, Trash2, Check,
  Settings2, Share2, Languages, FileJson, Webhook,
} from 'lucide-react';
import {
  aiAgentsService, AiAgent, AutocorrectConfig, HallucinationDetection, NoiseConfig,
  agentToolsService, AgentTool, agentDocumentsService, KnowledgeDocument, agentSettingsService,
  agentChannelsService, AgentChannel, ChannelType, agentPromptsService, AgentPrompt,
  DEFAULT_RINGBACK_PATH,
} from 'api/services/aiAgents.service';
import { isFollowUp, FollowUpOption } from './ToolEndpointEditor';
import { emailService } from 'api/services/email.service';
import { whatsappService } from 'api/services/whatsapp.service';
import { memoryProfilesService, MemoryProfile } from 'api/services/memoryProfiles.service';
import { agentCatalogService, AgentCatalog, CatalogOption } from 'api/services/agentCatalog.service';
import RuntimeDocumentEditor from './RuntimeDocumentEditor';
import CustomApiPushSection from './CustomApiPushSection';
import CallCategoryField, { traiComplianceOn } from 'components/Compliance/CallCategoryField';
import { useAuth } from 'hooks/useAuth';
import { CATEGORY_ADMINS, hasRole } from 'pages/Compliance/traiRules';
import { BusinessSwitcher } from 'components/Common/BusinessSwitcher';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

// What each lead-extraction provider falls back to when the agent names nothing.
// Mirrors LeadExtractionProvider on the backend — shown as placeholders so the
// boxes can stay empty, which is what "inherit" looks like.
const LEAD_MODEL_HINT: Record<string, string> = {
  OPENAI: 'gpt-4o-mini',
  ANTHROPIC: 'claude-haiku-4-5',
  GEMINI: 'gemini-2.0-flash',
  AZURE_OPENAI: 'your deployment name — required',
  OPENAI_COMPATIBLE: 'required',
};
const LEAD_BASE_URL_HINT: Record<string, string> = {
  OPENAI: 'https://api.openai.com/v1',
  ANTHROPIC: 'https://api.anthropic.com/v1',
  GEMINI: 'https://generativelanguage.googleapis.com/v1beta',
  AZURE_OPENAI: 'https://<resource>.openai.azure.com — required',
  OPENAI_COMPATIBLE: 'required',
};
// The two with no sane default: guessing one would aim a customer's transcripts
// at a vendor they never chose.
const NEEDS_EXPLICIT_TARGET = ['AZURE_OPENAI', 'OPENAI_COMPATIBLE'];

// Channel types + the address key each one binds (mirrors the classic Edit page).
const CHANNELS: ChannelType[] = ['VOICE', 'CHAT', 'WHATSAPP', 'WHATSAPP_CALL', 'RCS', 'EMAIL'];
const CHANNEL_KEY: Record<ChannelType, string> = {
  VOICE: 'did', CHAT: 'widget_id', WHATSAPP: 'waba_number', WHATSAPP_CALL: 'number', RCS: 'rcs_agent_id', EMAIL: 'from_address',
};

// Section registry — drives the sticky jump-nav, active-section highlight, and
// expand/collapse-all. Order matches the rendered sections. `runtime` shows only for
// runtime-linked agents, so the nav filters it out when absent.
const SECTIONS: { id: string; label: string }[] = [
  { id: 'identity', label: 'Identity' },
  { id: 'general', label: 'General' },
  { id: 'customapi', label: 'Custom API' },
  { id: 'llm', label: 'LLM' },
  { id: 'stt', label: 'STT' },
  { id: 'autocorrect', label: 'Auto-correct' },
  { id: 'tts', label: 'TTS' },
  { id: 'conversation', label: 'Conversation' },
  { id: 'halluc', label: 'Hallucination' },
  { id: 'dtmf', label: 'DTMF' },
  { id: 'audio', label: 'Audio' },
  { id: 'tools', label: 'Tools' },
  { id: 'knowledge', label: 'Knowledge' },
  { id: 'memory', label: 'Memory' },
  { id: 'fillers', label: 'Fillers' },
  { id: 'ringback', label: 'Ringback' },
  { id: 'channels', label: 'Channels' },
  { id: 'languages', label: 'Languages' },
  { id: 'runtime', label: 'Runtime' },
];

// Open-state is lifted to the page so the nav can jump-open a section and expand/collapse
// all work; each Section reads its own open flag by id (falling back to defaultOpen).
type SectionOpenCtx = { openMap: Record<string, boolean>; setOpen: (id: string, open: boolean) => void };
const SectionOpenContext = React.createContext<SectionOpenCtx | null>(null);

/**
 * Full agent configuration — modelled on the production Yamurai agent
 * (`ECOCVAY_263_TFIVR`) and the voicex runtime config schema. Providers/models/voices
 * come from the capability catalog (GET /v1/agent-catalog) so they are PICKED, not typed;
 * everything else — the whole conversation/interruption surface, the interruption phrase
 * dictionaries, the STT auto-correction block, DTMF prompts and audio denoising — is here
 * as real fields. Each field is sent to the backend under the exact name the runtime uses.
 */
const AgentConfigEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const agentId = Number(id);
  const { currentOrganization, user } = useAuth();
  const categoryEditable = hasRole((currentOrganization?.role || user?.role)?.toLowerCase(), CATEGORY_ADMINS);
  const [agent, setAgent] = useState<AiAgent | null>(null);
  const [catalog, setCatalog] = useState<AgentCatalog>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A one-off confirmation, e.g. what stayed behind after a move. */
  const [notice, setNotice] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // Sub-resources — managed via their OWN endpoints (agent-scoped), independent of the
  // main Save. These are the cpass-side parts not carried in the raw runtime config_json.
  const [tools, setTools] = useState<AgentTool[]>([]);
  const [toolCatalog, setToolCatalog] = useState<AgentTool[]>([]);
  // Follow-up choices for send_email / send_whatsapp. Only STANDING campaigns are
  // offered: a one-shot run has already drained and would never send, which the
  // backend refuses — listing them would just move that refusal to after the call.
  const [emailCampaigns, setEmailCampaigns] = useState<FollowUpOption[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<FollowUpOption[]>([]);
  const [waCampaigns, setWaCampaigns] = useState<FollowUpOption[]>([]);
  const [waTemplates, setWaTemplates] = useState<FollowUpOption[]>([]);
  const [docs, setDocs] = useState<KnowledgeDocument[]>([]);
  const [memories, setMemories] = useState<MemoryProfile[]>([]);
  const [channels, setChannels] = useState<AgentChannel[]>([]);
  const [prompts, setPrompts] = useState<AgentPrompt[]>([]);
  const [developerMode, setDeveloperMode] = useState(false);

  const reloadTools = useCallback(async () => {
    const t = await agentToolsService.list(agentId); if (t.success) setTools(t.data || []);
  }, [agentId]);
  const reloadDocs = useCallback(async () => {
    const d = await agentDocumentsService.list(agentId); if (d.success) setDocs(d.data || []);
  }, [agentId]);
  const reloadChannels = useCallback(async () => {
    const c = await agentChannelsService.list(agentId); if (c.success) setChannels(c.data || []);
  }, [agentId]);
  const reloadPrompts = useCallback(async () => {
    const p = await agentPromptsService.list(agentId); if (p.success) setPrompts(p.data || []);
  }, [agentId]);

  useEffect(() => {
    (async () => {
      const [a, c, t, tc, dm, mem, ch, pr] = await Promise.all([
        aiAgentsService.get(agentId), agentCatalogService.get(),
        agentToolsService.list(agentId), agentSettingsService.toolCatalog(),
        agentSettingsService.getDeveloperMode(), memoryProfilesService.list(),
        agentChannelsService.list(agentId), agentPromptsService.list(agentId),
      ]);
      if (a.success && a.data) setAgent(a.data); else setError(a.message || 'Failed to load agent');
      if (c.success && c.data) setCatalog(c.data);
      if (t.success) setTools(t.data || []);
      if (tc.success) setToolCatalog(tc.data || []);
      if (dm.success) setDeveloperMode(!!dm.data?.developerMode);
      if (mem.success) setMemories(mem.data || []);
      if (ch.success) setChannels(ch.data || []);
      if (pr.success) setPrompts(pr.data || []);
      await reloadDocs();
      setLoading(false);
    })();
  }, [agentId, reloadDocs]);

  const toggleDeveloperMode = async () => {
    const next = !developerMode;
    const r = await agentSettingsService.setDeveloperMode(next);
    if (r.success) { setDeveloperMode(!!r.data?.developerMode); setOk(`Developer mode ${next ? 'enabled' : 'disabled'}`); }
    else setError(r.message || 'Only a provider admin can change developer mode');
  };

  // ── channels (own endpoints) ──
  const [newChannel, setNewChannel] = useState<ChannelType>('WHATSAPP');
  const [newChannelAddr, setNewChannelAddr] = useState('');
  const addChannel = async () => {
    const key = CHANNEL_KEY[newChannel];
    const body: AgentChannel = { channel: newChannel, enabled: true, channel_config: newChannelAddr ? { [key]: newChannelAddr } : {} };
    const res = await agentChannelsService.upsert(agentId, body);
    if (res.success) { setNewChannelAddr(''); reloadChannels(); } else setError(res.message || 'Failed to add channel');
  };
  const toggleChannel = async (c: AgentChannel) => {
    const res = await agentChannelsService.upsert(agentId, { ...c, enabled: !c.enabled });
    if (res.success) reloadChannels(); else setError(res.message || 'Failed to update channel');
  };
  const [moveTo, setMoveTo] = useState('');
  const [moving, setMoving] = useState(false);

  /**
   * Move this agent to another business.
   *
   * Confirmed first, and the confirmation names what STAYS: captures and call
   * events carry their own organisation — they record where a lead was actually
   * filed and under whose CRM decision — so they are deliberately not rewritten.
   * Somebody moving an agent should learn that here, not by finding a history
   * split in two afterwards.
   */
  const moveAgent = async () => {
    if (!moveTo || !agent?.id) return;
    if (!window.confirm(
      'Move this agent to the selected business?\n\n'
      + 'Its tools, prompts, documents and channels move with it. Leads and call '
      + 'records already captured stay with the business they were filed under.'
    )) return;
    setMoving(true);
    const res = await aiAgentsService.move(agent.id as number, moveTo);
    setMoving(false);
    if (!res.success) { setError(res.message || 'Could not move the agent'); return; }
    const left = (res.data?.capturesLeftBehind ?? 0) + (res.data?.callEventsLeftBehind ?? 0);
    setNotice(left > 0
      ? `Moved. ${res.data?.capturesLeftBehind ?? 0} lead(s) and ${res.data?.callEventsLeftBehind ?? 0} call record(s) stayed with the previous business.`
      : 'Moved.');
    setMoveTo('');
  };

  const removeChannel = async (c: AgentChannel) => {
    if (!c.id) return;
    const res = await agentChannelsService.remove(agentId, c.id);
    if (res.success) setChannels((l) => l.filter((x) => x.id !== c.id)); else setError(res.message || 'Failed to remove channel');
  };

  // ── languages / per-language prompts (own endpoints) ──
  const [newPrompt, setNewPrompt] = useState<AgentPrompt>({ language: '', system_prompt: '', greeting_message: '' });
  const savePrompt = async (p: AgentPrompt) => {
    if (!p.language.trim()) { setError('Language code is required (e.g. fr, sn)'); return; }
    const res = await agentPromptsService.upsert(agentId, p);
    if (res.success) { setNewPrompt({ language: '', system_prompt: '', greeting_message: '' }); reloadPrompts(); }
    else setError(res.message || 'Failed to save prompt');
  };
  const removePrompt = async (p: AgentPrompt) => {
    if (!p.id) return;
    const res = await agentPromptsService.remove(agentId, p.id);
    if (res.success) setPrompts((l) => l.filter((x) => x.id !== p.id)); else setError(res.message || 'Failed to remove prompt');
  };

  // Loaded once. These change far more slowly than the agent, and the tools list
  // should not wait on four calls to render.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [ec, et, wc, wt] = await Promise.all([
        emailService.listCampaigns(),
        emailService.listTemplates(),
        whatsappService.getCampaigns({ page: 0, size: 200 }),
        whatsappService.getTemplates(),
      ]);
      if (!alive) return;
      const opts = (rows: any[], nameKey = 'name'): FollowUpOption[] =>
        (rows || []).filter((r) => r?.id).map((r) => ({ id: String(r.id), name: String(r[nameKey] || r.id) }));
      if (ec.success) setEmailCampaigns(opts(((ec.data as any[]) || []).filter((c: any) => c.standing)));
      if (et.success) setEmailTemplates(opts((et.data as any[]) || []));
      const waRows: any[] = (wc as any)?.data?.content || (wc as any)?.data || [];
      setWaCampaigns(opts(waRows.filter((c: any) => c?.standing)));
      const waTpl: any[] = (wt as any)?.data?.content || (wt as any)?.data || [];
      setWaTemplates(opts(waTpl, 'templateName'));
    })().catch(() => { /* empty pickers, which the row explains */ });
    return () => { alive = false; };
  }, []);

  /**
   * Save which follow-up a tool sends.
   *
   * Exactly one of the two: setting a campaign clears the template and vice versa.
   * The backend prefers the campaign, so a leftover template would sit in the config
   * looking like it was going to be used.
   */
  const setFollowUp = async (t: AgentTool, key: 'campaign_id' | 'template_id', value: string) => {
    const other = key === 'campaign_id' ? 'template_id' : 'campaign_id';
    const execution_config = { ...(t.execution_config || {}) } as Record<string, any>;
    if (value) { execution_config[key] = value; delete execution_config[other]; }
    else delete execution_config[key];
    const res = await agentToolsService.upsert(agentId, { ...t, execution_config });
  };

  /**
   * When this tool runs: during the conversation, or after it.
   *
   * Per AGENT, not per tool, and that is the whole reason it is editable here.
   * The same tool belongs in different places for different businesses — a lead
   * whose CRM wants thirteen fields is worth ASKING for while the caller is on
   * the line, and one that wants a phone number is not worth lengthening the
   * call for. Until now the choice existed in the backend and could only be made
   * with SQL.
   *
   * Sent as null to mean "whatever this tool defaults to", so an agent that has
   * never been touched keeps following the default if it changes.
   */
  const setInvocation = async (t: AgentTool, value: string) => {
    const res = await agentToolsService.upsert(agentId, {
      ...t, invocation: value || null,
    } as any);
    if (res.success) reloadTools(); else setError(res.message || 'Failed to save when the tool runs');
  };

  // ── tools (own endpoints) ──
  const addToolFromCatalog = async (t: AgentTool) => {
    if (tools.some((x) => x.name === t.name)) return;
    const res = await agentToolsService.upsert(agentId, { ...t });
    if (res.success) reloadTools(); else setError(res.message || 'Failed to add tool');
  };
  const removeTool = async (t: AgentTool) => {
    if (!t.id) return;
    const res = await agentToolsService.remove(agentId, t.id);
    if (res.success) setTools((l) => l.filter((x) => x.id !== t.id)); else setError(res.message || 'Failed to remove tool');
  };
  const [customTool, setCustomTool] = useState<{ name: string; description: string; url: string; method: string }>({ name: '', description: '', url: '', method: 'POST' });
  const addCustomTool = async () => {
    if (!customTool.name.trim()) { setError('Custom tool needs a name'); return; }
    const body: AgentTool = {
      name: customTool.name.trim(), description: customTool.description, execution_type: 'api', enabled: true,
      parameters: { type: 'object', properties: {} },
      execution_config: customTool.url ? { url: customTool.url, method: customTool.method } : {},
    };
    const res = await agentToolsService.upsert(agentId, body);
    if (res.success) { setCustomTool({ name: '', description: '', url: '', method: 'POST' }); reloadTools(); }
    else setError(res.message || 'Failed to add tool');
  };

  // ── knowledge documents (own endpoints) ──
  const [newDoc, setNewDoc] = useState<KnowledgeDocument>({ filename: '', content: '' });
  const addDoc = async () => {
    if (!newDoc.filename.trim()) { setError('Document needs a filename'); return; }
    const res = await agentDocumentsService.add(agentId, newDoc);
    if (res.success) { setNewDoc({ filename: '', content: '' }); reloadDocs(); } else setError(res.message || 'Failed to add document');
  };
  const removeDoc = async (d: KnowledgeDocument) => {
    if (!d.id) return;
    const res = await agentDocumentsService.remove(agentId, d.id);
    if (res.success) setDocs((l) => l.filter((x) => x.id !== d.id)); else setError(res.message || 'Failed to remove document');
  };

  const set = (patch: Partial<AiAgent>) => setAgent((prev) => (prev ? { ...prev, ...patch } : prev));

  const opts = (dimension: string, provider?: string | null): CatalogOption[] => {
    const all = catalog[dimension] || [];
    if (!provider) return all;
    return all.filter((o) => o.provider === provider || o.provider == null);
  };

  const llmModels = useMemo(() => opts('LLM_MODEL', agent?.provider), [catalog, agent?.provider]);
  const ttsVoices = useMemo(() => opts('TTS_VOICE', agent?.tts_provider), [catalog, agent?.tts_provider]);
  const ttsModels = useMemo(() => opts('TTS_MODEL', agent?.tts_provider), [catalog, agent?.tts_provider]);
  const sttModels = useMemo(() => opts('STT_MODEL', agent?.stt_provider), [catalog, agent?.stt_provider]);

  // ── section open-state + jump nav (fast editing across 18 sections) ──
  const [openMap, setOpenMap] = useState<Record<string, boolean>>({ identity: true });
  const [activeSection, setActiveSection] = useState('identity');
  const setOpen = useCallback((sid: string, open: boolean) => setOpenMap((m) => ({ ...m, [sid]: open })), []);
  const expandAll = useCallback(() => setOpenMap(Object.fromEntries(SECTIONS.map((s) => [s.id, true]))), []);
  const collapseAll = useCallback(() => setOpenMap(Object.fromEntries(SECTIONS.map((s) => [s.id, false]))), []);
  const jump = useCallback((sid: string) => {
    setOpen(sid, true);
    setActiveSection(sid);
    requestAnimationFrame(() => document.getElementById(sid)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [setOpen]);

  // Highlight whichever section is near the top of the viewport as the user scrolls.
  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) setActiveSection((visible[0].target as HTMLElement).id);
    }, { rootMargin: '-15% 0px -75% 0px' });
    SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [loading, agent?.agent_uuid]);

  if (loading) return <LoadingSpinner />;
  if (!agent) return <div className="p-6"><ErrorMessage message={error || 'Agent not found'} /></div>;

  // Nested-config helpers — merge-patch so a single field edit keeps its siblings.
  const ac: AutocorrectConfig = agent.autocorrect || {};
  const setAc = (patch: Partial<AutocorrectConfig>) => set({ autocorrect: { ...ac, ...patch } });
  const hd: HallucinationDetection = agent.hallucination_detection || {};
  const setHd = (patch: Partial<HallucinationDetection>) => set({ hallucination_detection: { ...hd, ...patch } });
  const nc: NoiseConfig = agent.noise_config || {};
  const setNc = (patch: Partial<NoiseConfig>) => set({ noise_config: { ...nc, ...patch } });

  const save = async () => {
    setSaving(true); setError(null); setOk(null);
    const res = await aiAgentsService.update(agent.id as number, agent);
    setSaving(false);
    if (res.success) { setOk('Saved'); if (res.data) setAgent(res.data); }
    else setError(res.message || 'Failed to save');
  };

  const navSections = SECTIONS.filter((s) => s.id !== 'runtime' || agent.agent_uuid);
  return (
    <SectionOpenContext.Provider value={{ openMap, setOpen }}>
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <div className="flex items-center gap-3">
        <Bot className="w-7 h-7 text-indigo-600" />
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{agent.name || 'Agent'} — Configuration</h1>
      </div>

      {/* Sticky quick-nav: jump to any section, expand/collapse all, and save — always in reach. */}
      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-gray-50/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 overflow-x-auto flex gap-1.5 py-0.5">
            {navSections.map((s) => (
              <button key={s.id} type="button" onClick={() => jump(s.id)}
                className={`whitespace-nowrap text-xs px-2.5 py-1 rounded-full border transition ${activeSection === s.id
                  ? 'bg-indigo-600 border-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 shrink-0 border-l border-gray-200 dark:border-gray-700 pl-2">
            <button type="button" onClick={expandAll} title="Expand all sections" className="text-xs px-2 py-1 text-gray-500 hover:text-indigo-600">Expand</button>
            <button type="button" onClick={collapseAll} title="Collapse all sections" className="text-xs px-2 py-1 text-gray-500 hover:text-indigo-600">Collapse</button>
            <button type="button" onClick={save} disabled={saving}
              className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50">
              <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}
      {notice && (
        <div className="text-sm rounded-lg px-3 py-2 flex items-center justify-between gap-3
                        bg-green-50 text-green-900 dark:bg-green-900/20 dark:text-green-200">
          <span>{notice}</span>
          <button type="button" className="underline underline-offset-2" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {ok && <div className="text-sm text-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-3">{ok}</div>}

      {/* 1 — Identity & behaviour */}
      <Section id="identity" icon={<Bot className="w-4 h-4" />} title="Identity & behaviour" defaultOpen>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text label="Agent name" value={agent.name} onChange={(v) => set({ name: v })} />
          <Text label="Agent type" value={agent.agent_type} onChange={(v) => set({ agent_type: v })} placeholder="customer_support" />
        </div>
        <Text label="Greeting message" value={agent.greeting_message} onChange={(v) => set({ greeting_message: v })} placeholder="Hi, how can I help?" />
        <Area label="System prompt" value={agent.system_prompt} onChange={(v) => set({ system_prompt: v })} rows={6} mono />
        <Text label="Description" value={agent.description} onChange={(v) => set({ description: v })} />
        <Toggle label="Enabled" checked={agent.enabled ?? true} onChange={(v) => set({ enabled: v })} />
      </Section>

      {/* 1b — General & lead recovery */}
      <Section id="general" icon={<Settings2 className="w-4 h-4" />} title="General & lead recovery">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text label="Business id" value={agent.business_id} onChange={(v) => set({ business_id: v })} placeholder="optional" />
          <Field label="Runtime (cpass classification)">
            <select className={cls} value={agent.runtime || 'TEEOFF_VOICE'} onChange={(e) => set({ runtime: e.target.value as 'TEEOFF_VOICE' | 'TEEOFF_LLM' })}>
              <option value="TEEOFF_VOICE">TeeOff Voice</option>
              <option value="TEEOFF_LLM">TeeOff LLM</option>
            </select>
          </Field>
        </div>
        {agent.wsurl && (
          <Field label="wsurl (IVR binding — read-only)">
            <div className={`${cls} font-mono text-xs opacity-70`}>{agent.wsurl}</div>
          </Field>
        )}
        <div className="flex items-center gap-3">
          <Toggle label={`Developer mode ${developerMode ? 'on' : 'off'}`} checked={developerMode} onChange={toggleDeveloperMode} />
          <span className="text-[11px] text-gray-400">Gates custom API tool definitions for this business.</span>
        </div>

        <SubHead>Business</SubHead>
        <p className="text-[11px] text-gray-400 -mt-2">
          Which business owns this agent. An agent created before the business picker existed may be
          on the wrong one — this is how to correct it without going near the database.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <BusinessSwitcher value={moveTo} onChange={setMoveTo} />
          <button type="button" className="border rounded px-3 py-2 text-sm disabled:opacity-50"
                  disabled={!moveTo || moving} onClick={moveAgent}>
            {moving ? 'Moving…' : 'Move agent here'}
          </button>
        </div>

        <SubHead>Personalisation</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Text label="Call them what, if we don’t know their name"
                value={agent.caller_name_fallback || ''}
                onChange={(v) => set({ caller_name_fallback: v || null })}
                placeholder="there" />
        </div>
        <p className="text-[11px] text-gray-400 -mt-2 max-w-3xl">
          The caller’s name, company and your own contact fields are resolved before the call is
          placed and handed to the agent as plain values — so a prompt never risks reading a
          template out loud. This is the floor: it applies to campaign dials and the single-call
          button too, and a reply rule may override it for its own campaign.
        </p>

        <SubHead>What the runtime summarises</SubHead>
        <p className="text-[11px] text-gray-400 -mt-2">
          Sent with the transcript at the end of every call. This was previously fixed at
          &ldquo;make summary&rdquo; and invisible here, so instructions written into it could be
          overwritten by a push with nothing showing what had been lost.
        </p>
        <Field label="Post-call summary prompt">
          <textarea className={`${cls} font-mono text-xs`} rows={6}
            value={agent.post_call_summary_prompt || ''}
            placeholder={'Leave empty for "make summary".\n\nName the fields you want, one per line, to have them read off the transcript:\n  - join_reason\n  - biggest_frustration'}
            onChange={(e) => set({ post_call_summary_prompt: e.target.value })} />
          <p className="text-[11px] text-gray-400 mt-1">
            When this agent has no lead tool, the fields named here are what cpass extracts.
            A field list — bullets or a JSON example — is read; prose is not, so write the
            names plainly.
          </p>
        </Field>

        <SubHead>What reaches the CRM</SubHead>
        <p className="text-[11px] text-gray-400 -mt-2">
          Applies to both routes — leads the agent captures on the call and leads recovered from the
          transcript afterwards. Leave both empty and nothing changes.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Source">
            <input className={cls} value={agent.lead_source || ''}
              placeholder="Voice Agent"
              onChange={(e) => set({ lead_source: e.target.value || null })} />
            <p className="text-[11px] text-gray-400 mt-1">
              What every lead from this agent reports as its Source, here and in your CRM. Set it and
              it wins over whatever the agent&rsquo;s own tool sends &mdash; which is the point:
              without it one channel arrives under several names. Whether a lead was taken on the call
              or recovered afterwards is recorded separately and is not affected by this.
            </p>
          </Field>
          <Field label="Required before pushing to the CRM">
            <input className={cls} value={
                Array.isArray(agent.crm_required_fields)
                  ? agent.crm_required_fields.join(', ')
                  : (agent.crm_required_fields || '')
              }
              placeholder="nothing is required"
              onChange={(e) => set({ crm_required_fields: e.target.value || null })} />
            <p className="text-[11px] text-gray-400 mt-1">
              Field names, comma separated &mdash; e.g. <code>name, phone_primary</code>. A lead
              missing one of these is still saved here and shown on the Leads screen as
              &ldquo;Missing required fields&rdquo;, but nothing is sent to the CRM and nothing is
              retried. Worth setting because the CRM push matches on phone number: a thinner record
              merges into the good one rather than sitting beside it.
            </p>
          </Field>
        </div>

        <SubHead>Recover a lead after the call</SubHead>
        <p className="text-[11px] text-gray-400 -mt-2">If the caller hangs up before the agent files the lead, cpass can read one out of the transcript instead.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="When to recover">
            <select className={cls} value={agent.post_call_lead_mode || 'IF_MISSING'} onChange={(e) => set({ post_call_lead_mode: e.target.value as any })}>
              <option value="IF_MISSING">Only when the call produced no lead</option>
              <option value="OFF">Never</option>
              <option value="ALWAYS">Always, even if a lead exists</option>
            </select>
          </Field>
          <Field label="Extraction">
            <select className={cls}
              value={agent.lead_extraction_enabled === null || agent.lead_extraction_enabled === undefined ? '' : String(agent.lead_extraction_enabled)}
              onChange={(e) => set({ lead_extraction_enabled: e.target.value === '' ? null : e.target.value === 'true' })}>
              <option value="">Use the platform default</option>
              <option value="true">On for this agent</option>
              <option value="false">Off for this agent</option>
            </select>
          </Field>
          <Field label="Provider">
            <select className={cls} value={agent.lead_extraction_provider || ''}
              onChange={(e) => set({ lead_extraction_provider: e.target.value || null })}>
              <option value="">Use the platform default</option>
              <option value="OPENAI">OpenAI</option>
              <option value="ANTHROPIC">Anthropic</option>
              <option value="GEMINI">Google Gemini</option>
              <option value="AZURE_OPENAI">Azure OpenAI</option>
              <option value="OPENAI_COMPATIBLE">OpenAI-compatible gateway</option>
            </select>
          </Field>
          <Text label="Model (optional)" value={agent.lead_extraction_model || ''} onChange={(v) => set({ lead_extraction_model: v || null })}
            placeholder={LEAD_MODEL_HINT[agent.lead_extraction_provider || ''] || 'platform default'} />
          <Text label="API base URL (optional)" value={agent.lead_extraction_base_url || ''} onChange={(v) => set({ lead_extraction_base_url: v || null })}
            placeholder={LEAD_BASE_URL_HINT[agent.lead_extraction_provider || ''] || 'platform default'} />
        </div>
        {NEEDS_EXPLICIT_TARGET.includes(agent.lead_extraction_provider || '') && (
          <p className="text-[11px] text-amber-500">
            {agent.lead_extraction_provider === 'AZURE_OPENAI'
              ? 'Azure needs both: the base URL is your resource root, and the model is the deployment name — there is no default for either.'
              : 'A gateway has no default address or model. Extraction stays off until both are filled in.'}
          </p>
        )}
        <Field label={`API key${agent.lead_extraction_api_key_set ? ' — a key is stored' : ' — none stored, the platform key is used'}`}>
          <input className={cls} type="password" autoComplete="new-password"
            placeholder={agent.lead_extraction_api_key_set ? 'leave blank to keep the stored key' : 'platform key'}
            value={agent.lead_extraction_api_key || ''} onChange={(e) => set({ lead_extraction_api_key: e.target.value })} />
          <span className="block mt-1 text-[11px] text-gray-400">Never shown once saved. Leave blank to keep the current key.</span>
        </Field>
      </Section>

      {/* 1c — Custom API push binding. Saved through its own button, which
          re-reads the agent and changes custom_api_push ONLY (see the component). */}
      <Section id="customapi" icon={<Webhook className="w-4 h-4" />} title="Custom API push"
        subtitle="Send this agent's captured leads to a Custom API provider, with fixed values per agent.">
        <CustomApiPushSection agent={agent}
          onSaved={(patch) => setAgent((prev) => (prev ? { ...prev, ...patch } : prev))} />
      </Section>

      {/* 1d — TRAI call category: behind the trai-compliance build flag; a normal build is unchanged.
          Saved through its own button to /v1/compliance/categories, never with the agent. */}
      {traiComplianceOn() && (
        <Section id="traicategory" icon={<ShieldAlert className="w-4 h-4" />} title="Call category (TRAI)"
          subtitle="Default A2P category for calls this agent places when the campaign or request does not set one.">
          <CallCategoryField scope="agent" id={agentId} canEdit={categoryEditable} label="Category" />
        </Section>
      )}

      {/* 2 — LLM */}
      <Section id="llm" icon={<Cpu className="w-4 h-4" />} title="Language model">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select label="Provider" value={agent.provider} onChange={(v) => set({ provider: v, model: '' })} options={opts('LLM_PROVIDER')} />
          <Text label="Family" value={agent.family} onChange={(v) => set({ family: v })} placeholder="openai" />
          <Select label="Model" value={agent.model} onChange={(v) => set({ model: v })} options={llmModels} />
          <NumField label="Temperature" step="0.1" value={agent.temperature} onChange={(v) => set({ temperature: v })} />
          <NumField label="Max tokens" value={agent.max_tokens} onChange={(v) => set({ max_tokens: v })} />
          <NumField label="Top k" value={agent.top_k} onChange={(v) => set({ top_k: v })} />
          <NumField label="Top p" step="0.05" value={agent.top_p} onChange={(v) => set({ top_p: v })} />
          <NumField label="Min p" step="0.05" value={agent.min_p} onChange={(v) => set({ min_p: v })} />
          <NumField label="Frequency penalty" step="0.1" value={agent.frequency_penalty} onChange={(v) => set({ frequency_penalty: v })} />
          <NumField label="Presence penalty" step="0.1" value={agent.presence_penalty} onChange={(v) => set({ presence_penalty: v })} />
          <Text label="Base URL (custom endpoint)" value={agent.base_url} onChange={(v) => set({ base_url: v })} placeholder="optional" />
        </div>
      </Section>

      {/* 3 — STT */}
      <Section id="stt" icon={<Mic className="w-4 h-4" />} title="Transcription (speech-to-text)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select label="Provider" value={agent.stt_provider} onChange={(v) => set({ stt_provider: v, stt_model: '' })} options={opts('STT_PROVIDER')} />
          <Select label="Model" value={agent.stt_model} onChange={(v) => set({ stt_model: v })} options={sttModels} />
          <Select label="Language" value={agent.language} onChange={(v) => set({ language: v })} options={opts('LANGUAGE')} />
          <NumField label="Endpointing (ms)" value={agent.endpointing} onChange={(v) => set({ endpointing: v })} />
          <NumField label="Sampling rate" value={agent.sampling_rate} onChange={(v) => set({ sampling_rate: v })} />
          <Text label="Encoding" value={agent.encoding} onChange={(v) => set({ encoding: v })} placeholder="linear16" />
        </div>
        <Text label="Keyword boost (comma-separated)" value={agent.transcriber_keywords} onChange={(v) => set({ transcriber_keywords: v })} placeholder="brand names, domain terms" />
      </Section>

      {/* 4 — Auto-correction */}
      <Section id="autocorrect" icon={<Wand2 className="w-4 h-4" />} title="Auto-correction (post-transcription)"
        subtitle="A separate LLM pass that normalises noisy ASR against a dictionary of canonical terms.">
        <Toggle label="Enabled" checked={!!ac.enabled} onChange={(v) => setAc({ enabled: v })} />
        {ac.enabled && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Text label="Provider" value={ac.provider} onChange={(v) => setAc({ provider: v })} placeholder="openai | groq | gemini | azure_openai" />
              <Text label="Model" value={ac.model} onChange={(v) => setAc({ model: v })} placeholder="gpt-4o-mini" />
              <NumField label="Temperature" step="0.05" value={ac.temperature} onChange={(v) => setAc({ temperature: v })} />
              <NumField label="Max tokens" value={ac.max_tokens} onChange={(v) => setAc({ max_tokens: v })} />
              <Text label="Primary language" value={ac.primary_language} onChange={(v) => setAc({ primary_language: v })} placeholder="shona" />
              <Text label="Country" value={ac.country} onChange={(v) => setAc({ country: v })} placeholder="Zimbabwe" />
              <NumField label="Timeout (s)" step="0.5" value={ac.timeout} onChange={(v) => setAc({ timeout: v })} />
              <NumField label="Max history turns" value={ac.max_history_turns} onChange={(v) => setAc({ max_history_turns: v })} />
              <Text label="Base URL" value={ac.base_url} onChange={(v) => setAc({ base_url: v })} placeholder="optional" />
            </div>
            <PhraseList label="Languages" values={ac.languages} onChange={(v) => setAc({ languages: v })} placeholder="add language" />
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Toggle label="Apply before LLM" checked={ac.apply_before_llm !== false} onChange={(v) => setAc({ apply_before_llm: v })} />
              <Toggle label="Use history" checked={ac.use_history !== false} onChange={(v) => setAc({ use_history: v })} />
              <Toggle label="Include bot responses" checked={ac.include_bot_responses !== false} onChange={(v) => setAc({ include_bot_responses: v })} />
              <Toggle label="Detect intent" checked={!!ac.detect_intent} onChange={(v) => setAc({ detect_intent: v })} />
              <Toggle label="Cache corrections" checked={ac.cache_corrections !== false} onChange={(v) => setAc({ cache_corrections: v })} />
              <Toggle label="Fallback on error" checked={ac.fallback_on_error !== false} onChange={(v) => setAc({ fallback_on_error: v })} />
            </div>
            <Area label="Correction rules (prompt + canonical dictionary)" value={ac.correction_rules} onChange={(v) => setAc({ correction_rules: v })} rows={8} mono
              placeholder={'ecocash  ← ekocash, ecoucash, eco cash\nairtime  ← air time, ea time'} />
            <MapEditor label="Phoneme corrections (heard → correct)" value={ac.phoneme_corrections} onChange={(v) => setAc({ phoneme_corrections: v })} />
            <MapEditor label="Code-switching words (local phonetic → English)" value={ac.code_switching_words} onChange={(v) => setAc({ code_switching_words: v })} />
            <ListMapEditor label="Intent keywords (intent → keywords)" value={ac.intent_keywords} onChange={(v) => setAc({ intent_keywords: v })} />
          </div>
        )}
      </Section>

      {/* 5 — TTS */}
      <Section id="tts" icon={<Volume2 className="w-4 h-4" />} title="Voice (text-to-speech)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select label="Provider" value={agent.tts_provider} onChange={(v) => set({ tts_provider: v, tts_voice_id: '', tts_model: '' })} options={opts('TTS_PROVIDER')} />
          <Select label="Voice" value={agent.tts_voice_id} onChange={(v) => set({ tts_voice_id: v })} options={ttsVoices} />
          <Select label="Model" value={agent.tts_model} onChange={(v) => set({ tts_model: v })} options={ttsModels} />
          <Text label="Audio format" value={agent.audio_format} onChange={(v) => set({ audio_format: v })} placeholder="pcm | wav | mp3" />
          <NumField label="Buffer size" value={agent.buffer_size} onChange={(v) => set({ buffer_size: v })} />
          <NumField label="Similarity boost" step="0.05" value={agent.similarity_boost} onChange={(v) => set({ similarity_boost: v })} />
          <NumField label="Voice temperature" step="0.05" value={agent.tts_temperature} onChange={(v) => set({ tts_temperature: v })} />
        </div>
        <Toggle label="Cache synthesized audio" checked={agent.caching !== false} onChange={(v) => set({ caching: v })} />
      </Section>

      {/* 6 — Conversation & interruption */}
      <Section id="conversation" icon={<Sliders className="w-4 h-4" />} title="Conversation & interruption">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumField label="Words before interruption counts" value={agent.number_of_words_for_interruption} onChange={(v) => set({ number_of_words_for_interruption: v })} />
          <NumField label="Interruption backoff (ms)" value={agent.interruption_backoff_period} onChange={(v) => set({ interruption_backoff_period: v })} />
          <NumField label="Incremental delay (ms)" value={agent.incremental_delay} onChange={(v) => set({ incremental_delay: v })} />
          <NumField label="Hang up after silence (s)" value={agent.hangup_after_silence} onChange={(v) => set({ hangup_after_silence: v })} />
          <NumField label="Max call duration (s)" value={agent.call_terminate} onChange={(v) => set({ call_terminate: v })} />
          <NumField label="Utterance debounce (ms)" value={agent.utterance_debounce_ms} onChange={(v) => set({ utterance_debounce_ms: v })} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
          <Toggle label="Optimize latency" checked={!!agent.optimize_latency} onChange={(v) => set({ optimize_latency: v })} />
          <Toggle label="Use fillers" checked={!!agent.use_fillers} onChange={(v) => set({ use_fillers: v })} />
          <Toggle label="Signal-based interruption" checked={agent.enable_signal_based_interruption !== false} onChange={(v) => set({ enable_signal_based_interruption: v })} />
          <Toggle label="Speech-final interrupt" checked={!!agent.enable_speech_final_interrupt} onChange={(v) => set({ enable_speech_final_interrupt: v })} />
          <Toggle label="Hang up after LLM call" checked={!!agent.hangup_after_LLMCall} onChange={(v) => set({ hangup_after_LLMCall: v })} />
          <Toggle label="Combine interrupted messages" checked={!!agent.combine_interrupted_user_messages} onChange={(v) => set({ combine_interrupted_user_messages: v })} />
          <Toggle label="Transcript buffering" checked={!!agent.enable_transcript_buffering} onChange={(v) => set({ enable_transcript_buffering: v })} />
        </div>

        <SubHead>Backchanneling</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Toggle label="Backchanneling" checked={!!agent.backchanneling} onChange={(v) => set({ backchanneling: v })} />
          <NumField label="Message gap (s)" value={agent.backchanneling_message_gap} onChange={(v) => set({ backchanneling_message_gap: v })} />
          <NumField label="Start delay (s)" value={agent.backchanneling_start_delay} onChange={(v) => set({ backchanneling_start_delay: v })} />
        </div>

        <SubHead>Ambient noise</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Toggle label="Ambient noise" checked={!!agent.ambient_noise} onChange={(v) => set({ ambient_noise: v })} />
          <Text label="Ambient track" value={agent.ambient_noise_track} onChange={(v) => set({ ambient_noise_track: v })} placeholder="office-ambience" />
        </div>

        <SubHead>Still-there check</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Toggle label="Check if user online" checked={!!agent.check_if_user_online} onChange={(v) => set({ check_if_user_online: v })} />
          <NumField label="Trigger after (s)" value={agent.trigger_user_online_message_after} onChange={(v) => set({ trigger_user_online_message_after: v })} />
          <Text label="Are-you-there message" value={agent.check_user_online_message} onChange={(v) => set({ check_user_online_message: v })} placeholder="Hey, are you still there?" />
          <div className="flex items-end"><Toggle label="Repeat last message" checked={!!agent.check_user_online_message_repeat_last} onChange={(v) => set({ check_user_online_message_repeat_last: v })} /></div>
        </div>
        <Text label="Call cancellation prompt" value={agent.call_cancellation_prompt} onChange={(v) => set({ call_cancellation_prompt: v })} placeholder="optional" />

        <SubHead>Interruption classifier</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-end"><Toggle label="Classifier enabled" checked={!!agent.interruption_classifier_enabled} onChange={(v) => set({ interruption_classifier_enabled: v })} /></div>
          <NumField label="Dedup window (s)" step="0.5" value={agent.interruption_classifier_dedup_window_s} onChange={(v) => set({ interruption_classifier_dedup_window_s: v })} />
          <NumField label="Per-call cap" value={agent.interruption_classifier_per_call_cap} onChange={(v) => set({ interruption_classifier_per_call_cap: v })} />
          <NumField label="Ack recency (ms)" value={agent.post_completion_ack_recency_ms} onChange={(v) => set({ post_completion_ack_recency_ms: v })} />
        </div>

        <SubHead>Interruption phrase dictionaries</SubHead>
        <PhraseList label="Directive phrases (should interrupt)" values={agent.directive_phrases} onChange={(v) => set({ directive_phrases: v })} placeholder='e.g. "stop"' />
        <PhraseList label="Post-completion ack phrases (don’t interrupt)" values={agent.post_completion_ack_phrases} onChange={(v) => set({ post_completion_ack_phrases: v })} placeholder='e.g. "ok thanks"' />
        <PhraseList label="Backchannel phrases" values={agent.backchannel_phrases} onChange={(v) => set({ backchannel_phrases: v })} placeholder='e.g. "uh-huh"' />
        <PhraseList label="Keepalive phrases" values={agent.keepalive_phrases} onChange={(v) => set({ keepalive_phrases: v })} placeholder='e.g. "hello"' />
      </Section>

      {/* 7 — Hallucination detection */}
      <Section id="halluc" icon={<ShieldAlert className="w-4 h-4" />} title="Hallucination detection">
        <Toggle label="Enabled" checked={!!hd.enabled} onChange={(v) => setHd({ enabled: v })} />
        {hd.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
            <NumField label="Repetition threshold" value={hd.repetition_threshold} onChange={(v) => setHd({ repetition_threshold: v })} />
            <NumField label="Max unique-word ratio" step="0.05" value={hd.max_unique_word_ratio} onChange={(v) => setHd({ max_unique_word_ratio: v })} />
            <NumField label="Min words for analysis" value={hd.min_words_for_analysis} onChange={(v) => setHd({ min_words_for_analysis: v })} />
          </div>
        )}
      </Section>

      {/* 8 — DTMF / keypad */}
      <Section id="dtmf" icon={<Keyboard className="w-4 h-4" />} title="DTMF / keypad">
        <Text label="Keypad prompt (spoken)" value={agent.keypad_message} onChange={(v) => set({ keypad_message: v })} placeholder="Please enter the digits on your keypad…" />
        <Text label="No-input fallback message" value={agent.default_keypad_input_message} onChange={(v) => set({ default_keypad_input_message: v })} placeholder="keyboard input missing" />
      </Section>

      {/* 9 — Audio IO */}
      <Section id="audio" icon={<Waves className="w-4 h-4" />} title="Audio input / output">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Text label="Input provider" value={agent.input_provider} onChange={(v) => set({ input_provider: v })} placeholder="asterisk" />
          <Text label="Output provider" value={agent.output_provider} onChange={(v) => set({ output_provider: v })} placeholder="asterisk" />
          <Text label="Format" value={agent.io_format} onChange={(v) => set({ io_format: v })} placeholder="wav" />
          <NumField label="Sampling rate" value={agent.io_sampling_rate} onChange={(v) => set({ io_sampling_rate: v })} />
        </div>
        <SubHead>Noise reduction (input)</SubHead>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Toggle label="Enable denoiser" checked={!!nc.enable} onChange={(v) => setNc({ enable: v })} />
          <Text label="Method" value={nc.method} onChange={(v) => setNc({ method: v })} placeholder="spectral" />
          <NumField label="Aggressiveness" step="0.01" value={nc.aggressiveness} onChange={(v) => setNc({ aggressiveness: v })} />
          <NumField label="Highpass cutoff (Hz)" value={nc.highpass_cutoff} onChange={(v) => setNc({ highpass_cutoff: v })} />
        </div>
      </Section>

      {/* 10 — Tools (function calling) — managed via their own endpoints, saved on add/remove */}
      <Section id="tools" icon={<Wrench className="w-4 h-4" />} title={`Tools (${tools.length})`}
        subtitle="Functions the agent can call. Added/removed immediately — independent of Save configuration.">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Predefined tools</div>
          <div className="flex flex-wrap gap-2">
            {toolCatalog.map((t) => {
              const added = tools.some((x) => x.name === t.name);
              return (
                <button key={t.name} type="button" disabled={added} onClick={() => addToolFromCatalog(t)} title={t.description}
                  className={`text-xs px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1.5 ${added
                    ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'}`}>
                  {added ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {t.name}
                </button>
              );
            })}
            {toolCatalog.length === 0 && <span className="text-xs text-gray-400">No catalog tools.</span>}
          </div>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          {tools.length === 0 && <div className="px-4 py-6 text-center text-sm text-gray-400">No tools — the agent has no functions to call.</div>}
          {tools.map((t) => {
            const cfg = (t.execution_config || {}) as Record<string, any>;
            const followUp = isFollowUp(t.name);
            const chosen = cfg.campaign_id || cfg.template_id;
            const isEmail = t.name === 'send_email';
            const campaigns = isEmail ? emailCampaigns : waCampaigns;
            const templates = isEmail ? emailTemplates : waTemplates;
            return (
            <div key={t.id ?? t.name} className="px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {t.name}
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">{t.execution_type}</span>
                    {/* A follow-up tool with nothing chosen sends nothing. Without this
                        it reads as configured, which is the failure the tool exists to avoid. */}
                    {followUp && !chosen && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                            title="No campaign or template chosen — this tool will send nothing">
                        nothing to send
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">{t.description}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* When it runs. The backend already decided this per agent and
                      nothing on screen could change it — the choice existed only
                      in SQL. Blank means "this tool's default", so an agent
                      nobody has touched follows the default if it ever moves. */}
                  <select
                    className="text-xs border border-gray-200 dark:border-gray-700 rounded px-2 py-1
                               bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
                    value={(t as any).invocation || ''}
                    onChange={(e) => setInvocation(t, e.target.value)}
                    title="During the call is accurate but lengthens it; after the call costs the caller nothing but can only use what was already said."
                  >
                    <option value="">Default for this tool</option>
                    <option value="in_call">During the call</option>
                    <option value="post_call">After the call</option>
                  </select>
                  <button type="button" onClick={() => removeTool(t)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {followUp && (
                <div className="mt-3 pl-1 space-y-2">
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    {isEmail
                      ? 'Sent after the call, to the email address on the caller\'s contact record. A voice call gives us their number, not their address — with no email on file this is skipped and the reason recorded.'
                      : 'Sent after the call, to the number that called. Do-not-disturb and recency rules apply exactly as they do for a bulk campaign.'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Field label="Add them to a standing campaign">
                      <select className={cls} value={cfg.campaign_id || ''}
                              onChange={(e) => setFollowUp(t, 'campaign_id', e.target.value)}>
                        <option value="">— none —</option>
                        {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </Field>
                    <Field label="Or send a one-off from a template">
                      <select className={cls} value={cfg.template_id || ''}
                              onChange={(e) => setFollowUp(t, 'template_id', e.target.value)}>
                        <option value="">— none —</option>
                        {templates.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                    </Field>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    A standing campaign brings tracking, suppression and grouped reporting; a template
                    sends a one-off with none of that.
                    {campaigns.length === 0 && ` No standing campaigns yet — create one under ${isEmail ? 'Email' : 'WhatsApp'} → Campaigns and tick "Keep this campaign open".`}
                  </p>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {developerMode ? (
          <div className="border border-indigo-200 dark:border-indigo-800 rounded-lg p-4 space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Add a custom API tool</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Text label="Name" value={customTool.name} onChange={(v) => setCustomTool({ ...customTool, name: v })} placeholder="lookup_order" />
              <Text label="Description" value={customTool.description} onChange={(v) => setCustomTool({ ...customTool, description: v })} />
              <Text label="Endpoint URL" value={customTool.url} onChange={(v) => setCustomTool({ ...customTool, url: v })} placeholder="https://api.example.com/tool" />
              <Field label="Method">
                <select className={cls} value={customTool.method} onChange={(e) => setCustomTool({ ...customTool, method: e.target.value })}>
                  {['POST', 'GET', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <button type="button" onClick={addCustomTool} className="tl-btn tl-btn-primary tl-btn-sm"><Plus className="w-4 h-4" /> Add tool</button>
            {/* Was: "edited on the classic agent Edit page". That page (AIAgentEdit)
                has no route and is archived under docs/archive/frontend — the sentence
                sent people looking for a screen that cannot be opened. */}
            <p className="text-[11px] text-gray-400">JSON parameter schemas and header/auth wiring are not editable here yet — a custom tool gets a name, description, URL and method.</p>
          </div>
        ) : (
          <p className="text-[11px] text-gray-400">A provider admin can enable Developer mode for this business to define custom API tools.</p>
        )}
      </Section>

      {/* 11 — Knowledge base (RAG) — documents via their own endpoints */}
      <Section id="knowledge" icon={<BookOpen className="w-4 h-4" />} title={`Knowledge base (${docs.length})`}
        subtitle="RAG documents the agent can answer from. Added/removed immediately.">
        <Toggle label="Enable RAG knowledge base" checked={!!agent.kb_enabled} onChange={(v) => set({ kb_enabled: v })} />
        {!agent.kb_enabled && <p className="text-[11px] text-amber-600">RAG is off — enable it (and Save) for these documents to be used.</p>}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
          <Text label="Filename" value={newDoc.filename} onChange={(v) => setNewDoc({ ...newDoc, filename: v })} placeholder="bundles.txt" />
          <Area label="Content" value={newDoc.content} onChange={(v) => setNewDoc({ ...newDoc, content: v })} rows={5} mono placeholder="document text the agent should know" />
          <button type="button" onClick={addDoc} className="tl-btn tl-btn-primary tl-btn-sm"><Plus className="w-4 h-4" /> Add document</button>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          {docs.length === 0 && <div className="px-4 py-6 text-center text-sm text-gray-400">No documents.</div>}
          {docs.map((d) => (
            <div key={d.id ?? d.filename} className="px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-800 dark:text-gray-100">{d.filename}
                {d.status && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500">{d.status}</span>}
              </div>
              <button type="button" onClick={() => removeDoc(d)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Section>

      {/* 12 — Memory (org-level profile; saved with the agent) */}
      <Section id="memory" icon={<Brain className="w-4 h-4" />} title="Memory"
        subtitle="Cross-channel supermemory profile — remembered with Save configuration.">
        <Field label="Memory profile">
          <select className={cls} value={agent.memory_profile_id ?? ''} onChange={(e) => set({ memory_profile_id: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">None</option>
            {memories.map((m) => <option key={m.id} value={m.id}>{m.name}{(m as any).provider ? ` (${(m as any).provider})` : ''}</option>)}
          </select>
        </Field>
        {memories.length === 0 && <p className="text-[11px] text-gray-400">No memory profiles yet — create them under Memory Profiles.</p>}
      </Section>

      {/* 13 — Fillers */}
      <Section id="fillers" icon={<MessageSquare className="w-4 h-4" />} title="Fillers">
        <Text label="Filler message (spoken while thinking)" value={agent.filler_message} onChange={(v) => set({ filler_message: v })} placeholder="Let me check that for you…" />
        <p className="text-[11px] text-gray-400">The <strong>Use fillers</strong> toggle is in Conversation &amp; interruption. Per-tool filler messages live on each tool.</p>
        {/* The runtime has no global filler message — fillerMessage is a per-tool
            field (ToolParams). This one is stored in cpass and is NOT pushed, so
            say so rather than let it read as configured. */}
        <p className="text-[11px] text-amber-600 dark:text-amber-400">Stored in cpass only — the voice runtime reads a filler message per tool, not one for the agent. Set it on the tool under Tools.</p>
      </Section>

      {/* 14 — Ringback / realtime (VAD) */}
      {/* VAD is PER STT PROVIDER in the runtime: simplismart reads model/onset/
          offset/filter, the STT wrapper reads threshold/timeout, gemini_live
          reads speech-end timeout and VAD events. The backend writes each knob
          only into the provider that reads it, so the ones outside the chosen
          provider's set are stored and not pushed. Ringback and welcome delay
          exist only on a realtime agent's gemini_live block. */}
      <Section id="ringback" icon={<Radio className="w-4 h-4" />} title="Ringback & realtime VAD"
        subtitle="Ringback and welcome delay apply to realtime voice-to-voice agents. VAD is per STT provider — simplismart reads model/onset/offset/filter, the STT wrapper reads threshold/timeout, Gemini Live reads speech-end timeout and VAD events; the rest are stored but not sent.">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Text
            label="Ringback file"
            value={agent.ringback}
            onChange={(v) => set({ ringback: v })}
            placeholder={DEFAULT_RINGBACK_PATH}
          />
          <NumField label="Welcome delay (s)" step="0.1" value={agent.welcome_delay} onChange={(v) => set({ welcome_delay: v })} />
          <Text label="VAD model" value={agent.vad_model} onChange={(v) => set({ vad_model: v })} placeholder="silero" />
          <div className="md:col-span-3">
            <Text
              label="Welcome instruction (required for ringback)"
              value={agent.welcome_instruction}
              onChange={(v) => set({ welcome_instruction: v })}
              placeholder="Begin with Step 1 of your flow"
            />
            {/* The ringback is played inside the welcome bootstrap, and the
                runtime skips that bootstrap when neither this nor a greeting
                message is set — so the toggle above does nothing on its own.
                Agent 39 sat with play_ringback true and never rang. */}
            <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
              The agent must have a greeting for ringback to play at all. Unlike <strong>Greeting message</strong>
              {' '}(spoken verbatim), this is an instruction to the model — use it when the opening line needs to
              vary, for example when it says the caller&rsquo;s name.
            </p>
          </div>
          <NumField label="VAD onset" step="0.01" value={agent.vad_onset} onChange={(v) => set({ vad_onset: v })} />
          <NumField label="VAD offset" step="0.01" value={agent.vad_offset} onChange={(v) => set({ vad_offset: v })} />
          <NumField label="VAD threshold" step="0.01" value={agent.vad_threshold} onChange={(v) => set({ vad_threshold: v })} />
          <NumField label="VAD timeout (s)" step="0.1" value={agent.vad_timeout} onChange={(v) => set({ vad_timeout: v })} />
          <NumField label="Speech-end timeout (s)" step="0.1" value={agent.speech_end_timeout} onChange={(v) => set({ speech_end_timeout: v })} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
          {/* The switch, not the file. Setting a path alone turned nothing on:
              the runtime defaults play_ringback to false and goes straight to
              the welcome bootstrap. Turning it on with no file set fills in the
              one every configured agent already uses, so the toggle is enough
              on its own — clear the field to fall back to the runtime's
              synthesised 400 Hz ring instead. */}
          <Toggle
            label="Play ringback"
            checked={!!agent.play_ringback}
            onChange={(v) =>
              set(
                v && !agent.ringback
                  ? { play_ringback: v, ringback: DEFAULT_RINGBACK_PATH }
                  : { play_ringback: v },
              )
            }
          />
          <Toggle label="VAD filter" checked={!!agent.vad_filter} onChange={(v) => set({ vad_filter: v })} />
          <Toggle label="Enable VAD events" checked={agent.enable_vad_events !== false} onChange={(v) => set({ enable_vad_events: v })} />
        </div>
      </Section>

      {/* 15 — Channels (own endpoints) */}
      <Section id="channels" icon={<Share2 className="w-4 h-4" />} title={`Channels (${channels.length})`}
        subtitle="Where this agent is exposed. Added/removed immediately.">
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Channel">
            <select className={cls} value={newChannel} onChange={(e) => setNewChannel(e.target.value as ChannelType)}>
              {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <div className="flex-1 min-w-[180px]">
            <Text label={CHANNEL_KEY[newChannel]} value={newChannelAddr} onChange={setNewChannelAddr}
              placeholder={newChannel === 'VOICE' ? 'DID (uses the agent wsurl)' : `the ${CHANNEL_KEY[newChannel]}`} />
          </div>
          <button type="button" onClick={addChannel} className="tl-btn tl-btn-primary tl-btn-sm"><Plus className="w-4 h-4" /> Add channel</button>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg">
          {channels.length === 0 && <div className="px-4 py-6 text-center text-sm text-gray-400">Not exposed on any channel yet.</div>}
          {channels.map((c) => (
            <div key={c.id ?? c.channel} className="px-4 py-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{c.channel}
                  {c.status && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${c.status === 'BOUND' ? 'bg-green-100 text-green-700' : c.status === 'FAILED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>}
                </div>
                <div className="text-xs text-gray-400 font-mono">{c.binding_ref || '—'}</div>
              </div>
              <div className="flex items-center gap-3">
                <Toggle label="enabled" checked={c.enabled ?? false} onChange={() => toggleChannel(c)} />
                <button type="button" onClick={() => removeChannel(c)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 16 — Languages / per-language prompts (own endpoints) */}
      <Section id="languages" icon={<Languages className="w-4 h-4" />} title={`Languages (${prompts.length})`}
        subtitle="Per-language greeting + system prompt. The default language's prompt is the one in Identity.">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Add a language</div>
          <div className="grid grid-cols-1 md:grid-cols-[8rem_1fr] gap-2">
            <Text label="Code" value={newPrompt.language} onChange={(v) => setNewPrompt({ ...newPrompt, language: v })} placeholder="fr, sn" />
            <Text label="Greeting" value={newPrompt.greeting_message} onChange={(v) => setNewPrompt({ ...newPrompt, greeting_message: v })} placeholder="greeting in this language" />
          </div>
          <Area label="System prompt" value={newPrompt.system_prompt} onChange={(v) => setNewPrompt({ ...newPrompt, system_prompt: v })} rows={3} mono placeholder="system prompt in this language" />
          <button type="button" onClick={() => savePrompt(newPrompt)} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add language</button>
        </div>
        {prompts.map((p) => (
          <div key={p.id ?? p.language} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold uppercase text-gray-700 dark:text-gray-200">{p.language}</span>
              <button type="button" onClick={() => removePrompt(p)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <Text label="Greeting" value={p.greeting_message} onChange={(v) => setPrompts((l) => l.map((x) => x.id === p.id ? { ...x, greeting_message: v } : x))} />
            <Area label="System prompt" value={p.system_prompt} onChange={(v) => setPrompts((l) => l.map((x) => x.id === p.id ? { ...x, system_prompt: v } : x))} rows={3} mono />
            <button type="button" onClick={() => savePrompt(p)} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm">Save {p.language}</button>
          </div>
        ))}
      </Section>

      {/* 17 — Runtime document (push to the live voicex agent) — only for runtime-linked agents */}
      {agent.agent_uuid && (
        <Section id="runtime" icon={<FileJson className="w-4 h-4" />} title="Runtime document"
          subtitle="Push this configuration to the live voicex agent.">
          <RuntimeDocumentEditor agent={agent} onPushed={async () => { const a = await aiAgentsService.get(agentId); if (a.success && a.data) setAgent(a.data); }} />
        </Section>
      )}

      <div className="flex justify-end pb-8">
        <button onClick={save} disabled={saving} className="tl-btn tl-btn-primary disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save configuration'}
        </button>
      </div>
    </div>
    </SectionOpenContext.Provider>
  );
};

// ── shared styles + primitives ──────────────────────────────────────────────
const cls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none';

const num = (v: string): number | undefined => (v === '' ? undefined : Number(v));

const Section: React.FC<{ id: string; icon: React.ReactNode; title: string; subtitle?: string; defaultOpen?: boolean; children: React.ReactNode }> =
  ({ id, icon, title, subtitle, defaultOpen, children }) => {
    const ctx = useContext(SectionOpenContext);
    const open = ctx ? (ctx.openMap[id] ?? !!defaultOpen) : !!defaultOpen;
    const toggle = () => ctx?.setOpen(id, !open);
    return (
      <div id={id} className="scroll-mt-20 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <button onClick={toggle} className="w-full flex items-center gap-2 px-5 py-4 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <span className="text-indigo-600">{icon}</span>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{title}</span>
          {subtitle && <span className="hidden md:inline text-xs text-gray-400 ml-2 truncate">{subtitle}</span>}
        </button>
        {open && <div className="px-5 pb-5 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">{children}</div>}
      </div>
    );
  };

const SubHead: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 pt-2">{children}</h4>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</span>{children}</label>
);

const Text: React.FC<{ label: string; value?: string; onChange: (v: string) => void; placeholder?: string }> =
  ({ label, value, onChange, placeholder }) => (
    <Field label={label}><input className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} /></Field>
  );

const Area: React.FC<{ label: string; value?: string; onChange: (v: string) => void; rows?: number; mono?: boolean; placeholder?: string }> =
  ({ label, value, onChange, rows, mono, placeholder }) => (
    <Field label={label}>
      <textarea rows={rows || 4} className={`${cls}${mono ? ' font-mono text-xs' : ''}`} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </Field>
  );

const NumField: React.FC<{ label: string; value?: number; onChange: (v: number | undefined) => void; step?: string }> =
  ({ label, value, onChange, step }) => (
    <Field label={label}><input type="number" step={step} className={cls} value={value ?? ''} onChange={(e) => onChange(num(e.target.value))} /></Field>
  );

const Select: React.FC<{ label: string; value?: string; onChange: (v: string) => void; options: CatalogOption[] }> =
  ({ label, value, onChange, options }) => (
    <Field label={label}>
      <select className={cls} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {value && !options.some((o) => o.value === value) && <option value={value}>{value} (current)</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.usageCount > 0 ? ` · ${o.usageCount}` : ''}</option>)}
      </select>
    </Field>
  );

const Toggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 rounded" />
    {label}
  </label>
);

// Editable list of phrases as removable chips + an add box.
const PhraseList: React.FC<{ label: string; values?: string[]; onChange: (v: string[]) => void; placeholder?: string }> =
  ({ label, values, onChange, placeholder }) => {
    const [draft, setDraft] = useState('');
    const list = values || [];
    const add = () => { const t = draft.trim(); if (t && !list.includes(t)) onChange([...list, t]); setDraft(''); };
    return (
      <Field label={label}>
        <div className="flex flex-wrap gap-2 mb-2">
          {list.map((p, i) => (
            <span key={`${p}-${i}`} className="flex items-center gap-1 px-2 py-1 text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full">
              {p}
              <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
          {list.length === 0 && <span className="text-xs text-gray-400">none</span>}
        </div>
        <div className="flex gap-2">
          <input className={cls} value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
          <button type="button" onClick={add} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"><Plus className="w-4 h-4" /></button>
        </div>
      </Field>
    );
  };

// Editable key → value map.
const MapEditor: React.FC<{ label: string; value?: Record<string, string>; onChange: (v: Record<string, string>) => void }> =
  ({ label, value, onChange }) => {
    const entries = Object.entries(value || {});
    const [k, setK] = useState('');
    const [v, setV] = useState('');
    const add = () => { const key = k.trim(); if (!key) return; onChange({ ...(value || {}), [key]: v }); setK(''); setV(''); };
    const remove = (key: string) => { const next = { ...(value || {}) }; delete next[key]; onChange(next); };
    return (
      <Field label={label}>
        <div className="space-y-1 mb-2">
          {entries.map(([key, val]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-200">{key}</code>
              <span className="text-gray-400">→</span>
              <span className="flex-1 text-gray-700 dark:text-gray-300 truncate">{val}</span>
              <button type="button" onClick={() => remove(key)} className="text-red-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {entries.length === 0 && <span className="text-xs text-gray-400">none</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <input className={cls} value={k} placeholder="heard" onChange={(e) => setK(e.target.value)} />
          <input className={cls} value={v} placeholder="correct" onChange={(e) => setV(e.target.value)} />
          <button type="button" onClick={add} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"><Plus className="w-4 h-4" /></button>
        </div>
      </Field>
    );
  };

// Editable key → list map (value entered comma-separated).
const ListMapEditor: React.FC<{ label: string; value?: Record<string, string[]>; onChange: (v: Record<string, string[]>) => void }> =
  ({ label, value, onChange }) => {
    const entries = Object.entries(value || {});
    const [k, setK] = useState('');
    const [v, setV] = useState('');
    const add = () => {
      const key = k.trim(); if (!key) return;
      const arr = v.split(',').map((s) => s.trim()).filter(Boolean);
      onChange({ ...(value || {}), [key]: arr }); setK(''); setV('');
    };
    const remove = (key: string) => { const next = { ...(value || {}) }; delete next[key]; onChange(next); };
    return (
      <Field label={label}>
        <div className="space-y-1 mb-2">
          {entries.map(([key, arr]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <code className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-200">{key}</code>
              <span className="text-gray-400">→</span>
              <span className="flex-1 text-gray-700 dark:text-gray-300 truncate">{(arr || []).join(', ')}</span>
              <button type="button" onClick={() => remove(key)} className="text-red-500 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {entries.length === 0 && <span className="text-xs text-gray-400">none</span>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <input className={cls} value={k} placeholder="intent" onChange={(e) => setK(e.target.value)} />
          <input className={cls} value={v} placeholder="keyword, keyword" onChange={(e) => setV(e.target.value)} />
          <button type="button" onClick={add} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"><Plus className="w-4 h-4" /></button>
        </div>
      </Field>
    );
  };

export default AgentConfigEditor;
